import { withErrorBoundary } from 'app/components/error-boundary';
import { Text } from 'app/design/primitives/text';
import type { NavigationScreen } from 'app/types/navigation';
import { View } from 'react-native';
import { HubButton, HubTutorial, styles } from './components';
import { OfferHubDock } from './components/offer-hub-dock';
import { OfferHubHeader } from './components/offer-hub-header';
import { OfferHubLoadingNotice } from './components/offer-hub-loading-notice';
import { OfferHubManualTab } from './components/offer-hub-manual-tab';
import { OfferHubPhotoTab } from './components/offer-hub-photo-tab';
import { OfferHubStage } from './components/offer-hub-stage';
import { OfferHubStorageNotice } from './components/offer-hub-storage-notice';

import type { ComponentProps } from 'react';
import { useOfferHubState } from './hooks';

function OfferHubScreenComponent(props: ComponentProps<NavigationScreen<'offer-hub'>>) {
  const {
    userQuery,
    hasAccess,
    insets,
    tab,
    tutorialOverlayVisible,
    tutorialStep,
    canUseAI,
    setTutorialStep,
    dismissTutorial,
    visitedTabs,
    focused,
    bulkRoute,
    manualRoute,
    navigation,
    setActive,
    setTab,
    showError,
    candidates,
    roundError,
    failed,
    queue,
    swipeTutorialStep,
    swipeTutorialCard,
    t,
    setSwipeTutorialStep,
    currentItem,
    fetchingRound,
    startList,
    accepted,
    statistics,
    refreshStatistics,
    pending,
    helpTutorialVisible,
    swiping,
    replayTutorial,
    items,
    round,
    selectTab,
    replaySwipeTutorial,
    close,
    session,
    tutorialVisible,
    swipeTutorialSwiping,
    loaded,
    replayingSwipeTutorial,
    completed,
    swipeCard,
    decide,
    theme,
    inProgress,
    overviewActive,
  } = useOfferHubState(props);
  if (userQuery.isPending || !hasAccess) return null;
  return (
    <View style={[styles.screen, { paddingTop: insets.top, paddingBottom: tab === 'suggestions' ? insets.bottom : 0 }]}>
      <View
        style={styles.container}
        accessibilityElementsHidden={tutorialOverlayVisible}
        importantForAccessibility={tutorialOverlayVisible ? 'no-hide-descendants' : 'auto'}
      >
        <OfferHubHeader
          helpTutorialVisible={helpTutorialVisible}
          t={t}
          swiping={swiping}
          setActive={setActive}
          replayTutorial={replayTutorial}
          items={items}
          round={round}
          tutorialOverlayVisible={tutorialOverlayVisible}
          tab={tab}
          selectTab={selectTab}
          canUseAI={canUseAI}
          replaySwipeTutorial={replaySwipeTutorial}
          close={close}
        />
        {visitedTabs.includes('photos') && canUseAI ? (
          <OfferHubPhotoTab tab={tab} bulkRoute={bulkRoute} navigation={navigation} focused={focused} />
        ) : null}
        {visitedTabs.includes('manual') ? (
          <OfferHubManualTab
            tab={tab}
            manualRoute={manualRoute}
            navigation={navigation}
            setActive={setActive}
            setTab={setTab}
          />
        ) : null}
        <View
          style={[styles.flex, tab !== 'suggestions' && { display: 'none' }]}
          accessibilityElementsHidden={tab !== 'suggestions'}
          importantForAccessibility={tab !== 'suggestions' ? 'no-hide-descendants' : 'auto'}
        >
          {showError ? <OfferHubStorageNotice t={t} queue={queue} session={session} /> : null}
          {candidates.isError || roundError ? <OfferHubLoadingNotice t={t} startList={startList} /> : null}
          <OfferHubStage
            swiping={swiping}
            tutorialVisible={tutorialVisible}
            swipeTutorialSwiping={swipeTutorialSwiping}
            loaded={loaded}
            t={t}
            replayingSwipeTutorial={replayingSwipeTutorial}
            swipeTutorialStep={swipeTutorialStep}
            swipeTutorialCard={swipeTutorialCard}
            tab={tab}
            focused={focused}
            setSwipeTutorialStep={setSwipeTutorialStep}
            currentItem={currentItem}
            completed={completed}
            accepted={accepted}
            navigation={navigation}
            statistics={statistics}
            refreshStatistics={refreshStatistics}
            pending={pending}
            items={items}
            swipeCard={swipeCard}
            showError={showError}
            decide={decide}
            theme={theme}
            inProgress={inProgress}
            round={round}
            overviewActive={overviewActive}
          />
          {failed.length ? (
            <View style={styles.notice}>
              <Text variant="bodySmall" color="text.error">
                {t('failedTitle')}
              </Text>
              <HubButton tone="ghost" onPress={() => queue?.retry()}>
                {t('retry')}
              </HubButton>
            </View>
          ) : null}
          <OfferHubDock
            loaded={loaded}
            replayingSwipeTutorial={replayingSwipeTutorial}
            swipeTutorialStep={swipeTutorialStep}
            swipeTutorialCard={swipeTutorialCard}
            t={t}
            fetchingRound={fetchingRound}
            showError={showError}
            setSwipeTutorialStep={setSwipeTutorialStep}
            startList={startList}
            completed={completed}
            close={close}
            swiping={swiping}
            swipeCard={swipeCard}
            currentItem={currentItem}
            inProgress={inProgress}
          />
        </View>
      </View>
      {tutorialOverlayVisible ? (
        <HubTutorial
          step={tutorialStep}
          topInset={insets.top}
          bottomInset={insets.bottom}
          canUseAI={canUseAI}
          onStepChange={setTutorialStep}
          onDismiss={dismissTutorial}
        />
      ) : null}
    </View>
  );
}
export const OfferHubScreen = withErrorBoundary(OfferHubScreenComponent);
